import React, { useState, useEffect } from 'react';
import { Copy, CheckCircle, Phone, ToggleLeft, ToggleRight, ChevronDown, ChevronUp } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ForwardingSetup() {
  const [virtualNumber, setVirtualNumber] = useState('');
  const [forwarding, setForwarding] = useState('unanswered');
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    // Load virtual number from API
    const token = localStorage.getItem('cia_token');
    if (token) {
      fetch(`${API_BASE}/provisioning/my-number`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(r => r.json())
      .then(data => {
        if (data.phone_number) setVirtualNumber(data.phone_number);
      })
      .catch(() => {});
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!virtualNumber) return null;

  // Generate forwarding codes
  const cleaned = virtualNumber.replace('+', '').replace(/\s/g, '');
  const dial = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned : `+${cleaned}`;

  const codes = {
    activate: {
      unanswered: `*61*${dial}*11*10#`,
      all:        `*21*${dial}#`,
      busy:       `*67*${dial}#`,
    },
    deactivate: {
      unanswered: `##61#`,
      all:        `##21#`,
      busy:       `##67#`,
    }
  };

  const labels = {
    unanswered: 'When unanswered (Recommended)',
    all:        'All calls',
    busy:       'When busy',
  };

  const copyCode = (code, key) => {
    navigator.clipboard.writeText(code);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const CodeRow = ({ label, activate, deactivate, codeKey }) => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
      gap: 8,
      padding: '10px 0',
      borderBottom: '1px solid var(--border)',
      alignItems: 'center',
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <code style={{
          fontSize: 12, fontFamily: 'var(--mono)',
          background: 'var(--bg-primary)', padding: '4px 8px',
          borderRadius: 4, color: 'var(--green)', flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{activate}</code>
        <a
          href={`tel:${activate}`}
          title="Dial now"
          style={{ color: 'var(--green)', flexShrink: 0, fontSize: 14 }}
        >📞</a>
        <button
          onClick={() => copyCode(activate, `act-${codeKey}`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied === `act-${codeKey}` ? 'var(--green)' : 'var(--text-muted)', flexShrink: 0 }}
        >
          {copied === `act-${codeKey}` ? <CheckCircle size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <code style={{
          fontSize: 12, fontFamily: 'var(--mono)',
          background: 'var(--bg-primary)', padding: '4px 8px',
          borderRadius: 4, color: 'var(--red)', flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{deactivate}</code>
        <a
          href={`tel:${deactivate}`}
          title="Dial now"
          style={{ color: 'var(--red)', flexShrink: 0, fontSize: 14 }}
        >📞</a>
        <button
          onClick={() => copyCode(deactivate, `deact-${codeKey}`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied === `deact-${codeKey}` ? 'var(--green)' : 'var(--text-muted)', flexShrink: 0 }}
        >
          {copied === `deact-${codeKey}` ? <CheckCircle size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Header */}
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Phone size={16} color="var(--blue)" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Call Forwarding Setup</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Your Aizaan number: <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--mono)' }}>{virtualNumber}</strong>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, padding: '3px 8px', borderRadius: 99,
            background: 'rgba(0,200,150,0.1)', color: 'var(--green)', fontWeight: 600
          }}>Active</span>
          {expanded ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16 }}>
          {/* Quick copy for recommended */}
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 14,
            marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              ⭐ Recommended — dial on your phone to activate forwarding when unanswered (5 seconds)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{
                fontSize: 16, fontFamily: 'var(--mono)', fontWeight: 700,
                color: 'var(--green)', letterSpacing: '0.05em', flex: 1,
              }}>
                {codes.activate.unanswered}
              </code>
              <a
                href={`tel:${codes.activate.unanswered}`}
                className="ob-btn ob-btn-primary"
                style={{ fontSize: 13, padding: '8px 16px', whiteSpace: 'nowrap', textDecoration: 'none' }}
              >
                📞 Dial now
              </a>
              <button
                className="ob-btn ob-btn-ghost"
                onClick={() => copyCode(codes.activate.unanswered, 'recommended')}
                style={{ fontSize: 13, padding: '8px 16px', whiteSpace: 'nowrap' }}
              >
                {copied === 'recommended' ? '✓ Copied!' : <><Copy size={13} /> Copy</>}
              </button>
            </div>
          </div>

          {/* Full code table */}
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
              gap: 8, paddingBottom: 8,
              borderBottom: '2px solid var(--border)',
              marginBottom: 4,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scenario</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activate</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deactivate</div>
            </div>

            <CodeRow
              label="When unanswered"
              activate={codes.activate.unanswered}
              deactivate={codes.deactivate.unanswered}
              codeKey="unanswered"
            />
            <CodeRow
              label="All calls"
              activate={codes.activate.all}
              deactivate={codes.deactivate.all}
              codeKey="all"
            />
            <CodeRow
              label="When busy"
              activate={codes.activate.busy}
              deactivate={codes.deactivate.busy}
              codeKey="busy"
            />
          </div>

          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.6 }}>
            Dial the <span style={{ color: 'var(--green)' }}>activate</span> code on your phone to start forwarding.
            Dial the <span style={{ color: 'var(--red)' }}>deactivate</span> code to stop forwarding at any time.
            Works on Android, iPhone, and most landlines.
          </p>
        </div>
      )}
    </div>
  );
}
